import { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePayslipNavigateWithQuery } from "../hooks/usePayslipNavigateWithQuery.js"
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Navbar } from "react-bootstrap";
import { useNavigate, useLocation, Link } from "react-router-dom";
import '../styles/Navigation.css';
import { useTranslation } from "react-i18next";
import logo from '../assets/logo-transparent.png';
import darkLogo from '../assets/logo-dark.png';

function NavBar({menuOpen, setMenuOpen}){
  const isDark = (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const inProduction = process.env.REACT_APP_ENV === "production";
  const [loggedOut, setLoggedOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPayslip = location.pathname.startsWith("/laskelma");
  const isInfo = location.pathname.startsWith("/instructions");
  const {i18n, t} = useTranslation();
  const setLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  };

  useEffect(() =>{
    const handler = (e) =>{
      if(e.key === "Escape"){
        setMenuOpen(null);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  },[]);

  const navigateWithParams = usePayslipNavigateWithQuery();

  const handleDefaultNav = (e, to, opts = {}) => {
    setMenuOpen(null); // probably not necessary

    if(opts.logout) { 
      navigate(to);
      setLoggedOut(true);
      //window.location.search = ""
    } else {
      navigateWithParams(to);
    }
  };

  const handleMobileNav = (e, to, opts = {}) =>{
    setMenuOpen(null);

    if(opts.logout) {
      navigate(to); 
      setLoggedOut(true); 
    } else {
      navigateWithParams(to);
    }
  };

  return(
    <Navbar className="navBar d-flex align-items-center justify-content-between position-sticky pe-sm-2 ps-lg-3 pe-lg-4 pe-xl-5" sticky="top">
      <div className="container-fluid d-flex align-items-center justify-content-between">
        <Navbar.Brand as={Link} to={loggedOut ? 'https://www.netboxfinland.fi/' : "/laskelma/"} className="col-lg-2 navLogo" target={loggedOut ? "_blank" : ''}>
          <img src={isDark ? darkLogo : logo} alt="Netbox logo" title="Netbox"/>
        </Navbar.Brand>

        {/* Mobile dropdown navigation btns */}
        <div className="d-flex d-lg-none gap-sm-2 gap-md-1">
          {!loggedOut &&
            <button className={`pageBtn ${menuOpen === 'page' ? 'open' : ''}`} onClick={() => {!menuOpen ? setMenuOpen('page') : setMenuOpen(null);} }
              aria-expanded={menuOpen === 'page'} aria-controls="mobilePageMenu">
              <p className="p-0 m-0"><span className="pageTextShort">{t("nav.mobileMenuBtn.menu")}</span><span className="pageTextLong">{t("nav.mobileMenuBtn.longMenu")}</span></p>
            </button>
          }
          <button className={`langBtn float-end ${menuOpen === 'lang' ? 'open' : ''}`} onClick={() => {!menuOpen ? setMenuOpen('lang') : setMenuOpen(null);} }
            aria-expanded={menuOpen === 'lang'} aria-controls="mobileLangMenu" aria-label={t("nav.langMenu")}>
            <i aria-hidden="true" className="bi bi-globe2"></i>
            <span className="visually-hidden">{t("nav.langMenu")}</span>
          </button>
        </div>

        {/* Mobile nav */}
        <ul id="mobilePageMenu" className={`navList d-lg-none m-0 p-0 ${menuOpen === 'page' ? "open" : ""}`}>
          <li><button className={isPayslip ? 'active' : ''} onClick={(e) => {
            if(isPayslip) setMenuOpen(null); // No navigation if currently active
            else handleMobileNav(e, "/laskelma");
          }}>
            {t("nav.payslips")}
          </button></li>
          {inProduction &&
            <li><button className={isInfo ? 'active' : ''} onClick={(e) => {
              if(isInfo) setMenuOpen(null); // No navigation if currently active
              else handleMobileNav(e, "/instructions");
            }}>
              {t("nav.instructions")}
            </button></li>
          }
          <li><button onClick={(e) => handleMobileNav(e, "/logout", { logout: true }) }>
            {t("nav.logout")}
          </button></li>
        </ul>

        <ul id="mobileLangMenu" className={`navList d-lg-none m-0 p-0 ${menuOpen === 'lang' ? 'open' : ''}`}>
          <li><button value="fi" onClick={()=> {setLanguage("fi"); setMenuOpen(null);}}
            aria-current={i18n.language === "fi" ? "true" : undefined} className={i18n.language === "fi" ? 'active' : ''}>
              Suomi</button></li>
          <li><button value="sv" onClick={()=> {setLanguage("sv"); setMenuOpen(null);}}
            aria-current={i18n.language === "sv" ? "true" : undefined} className={i18n.language === "sv" ? 'active' : ''}>
            Svenska</button></li>
          <li><button value="en" onClick={()=> {setLanguage("en"); setMenuOpen(null);}}
            aria-current={i18n.language === "en" ? "true" : undefined} className={i18n.language === "en" ? 'active' : ''}>
            English</button></li>
        </ul>

        {/* Overlay to block actions when menu open */}
        {menuOpen && (
          <div className="mobileMenuOverlay" onClick={() => setMenuOpen(null)}/>
        )}


        {/* Desktop/Tablet nav */}
        <div className="d-none d-lg-flex align-items-lg-center justify-content-lg-between navBarGap">
          <nav aria-label={t("nav.langMenu")} className="d-flex justify-content-between align-items-center">
            <ul className="navList m-0 p-0">
              <li><button value="fi" onClick={()=> setLanguage("fi")} disabled={i18n.language === "fi"}
               aria-current={i18n.language === "fi" ? "true" : undefined} className={i18n.language === "fi" ? 'active' : ''}>
                Suomi</button></li>
              <li><button value="sv" onClick={()=> setLanguage("sv")} disabled={i18n.language === "sv"}
               aria-current={i18n.language === "sv" ? "true" : undefined}className={i18n.language === "sv" ? 'active' : ''}>
                Svenska</button></li>
              <li><button value="en" onClick={()=> setLanguage("en")} disabled={i18n.language === "en"}
                aria-current={i18n.language === "en" ? "true" : undefined} className={i18n.language === "en" ? 'active' : ''}>
                English</button></li>
            </ul>
          </nav>
          {!loggedOut &&
            <nav aria-label={t("nav.mobileMenuBtn.longMenu")} className="d-flex justify-content-end align-items-center" style={{flexShrink:0, minWidth:'26.75rem'}}>
              <ul className="navList m-0 p-0 textCenter">
                <li><button className={isPayslip ? 'active' : ''} onClick={(e) => { if(!isPayslip) handleDefaultNav(e, "/laskelma"); }}>
                  {t("nav.payslips")}
                </button></li>
                {inProduction &&
                  <li><button className={isInfo ? 'active' : ''} onClick={(e) => { if(!isInfo) handleDefaultNav(e, "/instructions"); }}>
                    {t("nav.instructions")}
                  </button></li>
                }
                <li><button onClick={(e) => handleDefaultNav(e, "/logout", { logout: true }) }>
                  {t("nav.logout")}
                </button></li>
              </ul>
            </nav>
          }
        </div>
      </div>
    </Navbar>
  );
}

export default NavBar;
